/** Permitting a customer - **ADR-0160**, on ADR-0040's sharing agreement.
 *
 * `POST /supplier-exchange/agreements` has existed since ADR-0040 and the seed called the service;
 * no screen called the route, so a supplier could revoke a permission from a card and could not give
 * one (customer guide 11, section 4: "through the API; no screen"). This is that control, with the
 * route's own refusals.
 *
 * ## Whose act, and who is named
 *
 * **The supplier's, and only an Admin or Approver's** (`MAY_DECIDE`, as the route): permitting a
 * customer to copy your figures into their filing is a statement about your own operations. A
 * Contributor, Viewer or Auditor is told who can and sees no form (design rule 6). The supplier is
 * the session's workspace - `AgreementIn` has no field for it - and the customer is chosen from the
 * workspaces that have **already asked this one for a figure** (`knownBuyers`), for ADR-0151's reason
 * in the other direction: a workspace id is learned from a row this workspace owns, there is no
 * directory of other tenants to search, and a typed id naming a stranger would put a permission in a
 * company that never asked.
 *
 * ## What is offered
 *
 * Two uses, **neither ticked**: reading the answers to the customer's own requests, and copying an
 * accepted answer into their inventory. `disclose` is a third use the API can record and **no check
 * in the code reads**, so the form does not offer a control that constrains nothing; an agreement
 * that already carries it still shows it on its card. The period is a start date and an optional end
 * date, and the buyer's copy is checked against the period a figure is *for*, not today.
 *
 * ## An agreement is never edited
 *
 * The server refuses a second live agreement with the same customer. So when one exists the form
 * says so and offers nothing: revoke it on its card, then grant the new one, so the record says what
 * was permitted when (an edited agreement cannot answer that).
 *
 * ## Not retried
 *
 * A grant that did not complete may exist; the failure sentence says to look at the list.
 */

import { useState } from "react";

import { useMe } from "../api/auth";
import type { Agreement, Exchange } from "../api/suppliers";
import { useGrantAgreement } from "../api/suppliers";
import { WriteFailed } from "../components/Loaded";
import { knownBuyers, liveGrantTo } from "./supplierPermission";
import { workspaceTail } from "./supplierLabels";
import styles from "./Screen.module.css";
import own from "./Suppliers.module.css";

/** Mirrored from `api/suppliers.py`'s `MAY_DECIDE`. */
const MAY_DECIDE: readonly string[] = ["admin", "approver"];

const USES = [
  { key: "read", label: "Read the answers to its own requests" },
  { key: "inventory_copy", label: "Copy an accepted answer into its inventory" },
] as const;

type UseKey = (typeof USES)[number]["key"];

export function GrantAgreement({
  rows,
  agreements,
}: {
  /** Every exchange row this workspace can read: the source of who has asked. */
  rows: readonly Exchange[];
  /** Every agreement it is a party to: the source of "already permitted". */
  agreements: readonly Agreement[];
}) {
  const { data: me } = useMe();
  const mayDecide = me ? MAY_DECIDE.includes(me.role) : undefined;
  const [open, setOpen] = useState(false);
  const [done, setDone] = useState(false);

  const buyers = knownBuyers(rows);
  // A workspace that is only ever the buyer has nothing to permit, and a control for it would be
  // noise in every buyer's Permissions panel. A workspace with no exchange at all is a supplier
  // nobody has asked yet, and is told so.
  if (buyers.length === 0 && rows.length > 0) return null;
  // While `/auth/me` is in flight nothing is said: a sentence about who can grant, shown to a reader
  // who can, would be a flash of the wrong thing.
  if (mayDecide === undefined) return null;
  if (!mayDecide) {
    return (
      <p className={styles.note} data-testid="grant-who-can">
        An Admin or Approver permits a customer to copy your figures. It is a statement about your own
        operations.
      </p>
    );
  }
  if (buyers.length === 0) {
    return (
      <p className={styles.note} data-testid="grant-no-buyer">
        <strong>No customer to permit yet.</strong> A customer is a workspace of its own, and EmitIQ has
        no directory of them, so a permission can be given to a customer that has already asked you for a
        figure. When one does, it is listed here.
      </p>
    );
  }

  return (
    <div className={own.form} data-testid="grant-area">
      {open ? (
        <GrantForm
          buyers={buyers}
          agreements={agreements}
          onDone={() => {
            setOpen(false);
            setDone(true);
          }}
          onCancel={() => setOpen(false)}
        />
      ) : (
        <>
          <button
            type="button"
            className={styles.secondary}
            data-testid="grant-open"
            onClick={() => {
              setDone(false);
              setOpen(true);
            }}
          >
            Permit a customer
          </button>
          {done ? (
            <p className={styles.note} role="status" data-testid="grant-done">
              <strong>Permitted.</strong> The customer sees it in its own workspace as soon as it reads
              its permissions. Nothing is sent by email.
            </p>
          ) : null}
        </>
      )}
    </div>
  );
}

function GrantForm({
  buyers,
  agreements,
  onDone,
  onCancel,
}: {
  buyers: ReadonlyArray<{ id: string; asked: number }>;
  agreements: readonly Agreement[];
  onDone: () => void;
  onCancel: () => void;
}) {
  const grant = useGrantAgreement();
  const [buyer, setBuyer] = useState("");
  const [uses, setUses] = useState<ReadonlySet<UseKey>>(new Set());
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [refusal, setRefusal] = useState<unknown>(null);

  const live = buyer ? liveGrantTo(agreements, buyer) : undefined;
  const orderWrong = from !== "" && to !== "" && to < from;
  const ready = buyer !== "" && live === undefined && uses.size > 0 && from !== "" && !orderWrong;

  function toggle(key: UseKey) {
    setUses((was) => {
      const next = new Set(was);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }

  function submit() {
    setRefusal(null);
    grant
      .mutateAsync({
        buyer_org_id: buyer,
        // In the order the form lists them, not the order they were ticked: the same agreement
        // should be the same body.
        permitted_uses: USES.filter((entry) => uses.has(entry.key)).map((entry) => entry.key),
        effective_from: from,
        ...(to ? { expires_on: to } : {}),
      })
      .then(onDone)
      .catch((failure: unknown) => setRefusal(failure));
  }

  return (
    <form
      className={own.form}
      data-testid="grant-agreement"
      onSubmit={(event) => {
        event.preventDefault();
        if (ready && !grant.isPending) submit();
      }}
    >
      <label className={styles.field}>
        <span>Which customer?</span>
        <select value={buyer} onChange={(event) => setBuyer(event.target.value)} data-testid="grant-buyer">
          <option value="">Choose a customer…</option>
          {buyers.map((entry) => (
            <option key={entry.id} value={entry.id}>
              Customer workspace {workspaceTail(entry.id)} · asked {entry.asked} time
              {entry.asked === 1 ? "" : "s"}
            </option>
          ))}
        </select>
      </label>

      {live ? (
        <p className={styles.note} role="alert" data-testid="grant-live-exists">
          <strong>You already permit this customer.</strong> An agreement is never edited: revoke the one
          below with <em>Revoke it</em>, then permit again, so the record says what was permitted when.
        </p>
      ) : null}

      <fieldset className={own.form} data-testid="grant-uses">
        <legend>What may it do?</legend>
        {USES.map((entry) => (
          <label key={entry.key}>
            <input
              type="checkbox"
              checked={uses.has(entry.key)}
              onChange={() => toggle(entry.key)}
              data-testid={`grant-use-${entry.key}`}
            />{" "}
            {entry.label}
          </label>
        ))}
      </fieldset>
      <p className={styles.note}>
        Reading an answer and copying it into an inventory are different permissions: you can allow the
        first and not the second. Neither is ticked for you.
      </p>

      <label className={styles.field}>
        <span>From</span>
        <input type="date" value={from} onChange={(event) => setFrom(event.target.value)} data-testid="grant-from" />
      </label>
      <label className={styles.field}>
        <span>Until (optional)</span>
        <input
          type="date"
          value={to}
          min={from || undefined}
          onChange={(event) => setTo(event.target.value)}
          data-testid="grant-to"
        />
      </label>
      {orderWrong ? (
        <p className={styles.note} role="alert" data-testid="grant-period-order">
          That ends before it starts.
        </p>
      ) : null}
      <p className={styles.note} data-testid="grant-dated-on-period">
        The customer&rsquo;s copy is checked against the period a figure is <em>for</em>, not the day
        they copy it: an answer about 2025 needs a permission that covers 2025.
      </p>
      <p className={styles.note} data-testid="grant-revocation-says">
        You can revoke it later with a reason. <strong>Revoking stops the next copy and un-makes
        nothing</strong>: figures the customer has already copied stay in their inventory and keep
        citing it.
      </p>

      <button
        type="submit"
        className={styles.primary}
        data-testid="grant-send"
        disabled={!ready || grant.isPending}
      >
        {grant.isPending ? "Permitting…" : "Permit this customer"}
      </button>
      <button type="button" className={styles.fixLink} onClick={onCancel}>
        Cancel
      </button>
      {/* **No retry.** A grant that did not complete may exist; the sentence sends the reader to the
          list rather than offering a button that would be refused as a second live agreement. */}
      <WriteFailed error={refusal} of="this permission" />
    </form>
  );
}
